import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { TimeClockEntry, timeClockAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface TimerContextType {
  activeTimer: TimeClockEntry | null;
  isLoading: boolean;
  elapsedSeconds: number;
  showCompletionModal: boolean;
  startTimer: (
    projectId?: number,
    taskId?: number,
    tagIds?: number[],
  ) => Promise<void>;
  stopTimer: () => Promise<void>;
  setTimerTask: (taskId: number | null) => Promise<void>;
  discardTimer: () => Promise<void>;
  refreshTimer: () => Promise<void>;
  openCompletionModal: () => void;
  closeCompletionModal: () => void;
  onTimerCompleted: () => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

interface TimerProviderProps {
  children: ReactNode;
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [activeTimer, setActiveTimer] = useState<TimeClockEntry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate elapsed seconds from timer start time
  const calculateElapsedSeconds = useCallback((timer: TimeClockEntry) => {
    if (!timer.started_at) return 0;
    const startTime = new Date(timer.started_at).getTime();
    const endTime = timer.ended_at
      ? new Date(timer.ended_at).getTime()
      : Date.now();
    return Math.floor((endTime - startTime) / 1000);
  }, []);

  // Fetch current timer from server
  const fetchCurrentTimer = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await timeClockAPI.getCurrentTimer();
      const timer = response.data.time_clock_entry;
      setActiveTimer(timer);

      if (timer) {
        setElapsedSeconds(calculateElapsedSeconds(timer));
        // If timer was stopped but not converted, show completion modal
        if (timer.status === 'stopped' && !timer.converted_to_time_entry_id) {
          setShowCompletionModal(true);
        }
      } else {
        setElapsedSeconds(0);
      }
    } catch (error) {
      console.error('Failed to fetch current timer:', error);
      setActiveTimer(null);
      setElapsedSeconds(0);
    }
  }, [isAuthenticated, calculateElapsedSeconds]);

  // Start the elapsed time counter
  const startElapsedCounter = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  }, []);

  // Stop the elapsed time counter
  const stopElapsedCounter = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Fetch timer on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentTimer();
    } else {
      setActiveTimer(null);
      setElapsedSeconds(0);
      stopElapsedCounter();
    }
  }, [isAuthenticated, fetchCurrentTimer, stopElapsedCounter]);

  // Manage elapsed counter based on timer state
  useEffect(() => {
    if (activeTimer?.status === 'started') {
      startElapsedCounter();
    } else {
      stopElapsedCounter();
    }

    return () => stopElapsedCounter();
  }, [activeTimer?.status, startElapsedCounter, stopElapsedCounter]);

  // Start a new timer
  const startTimer = async (
    projectId?: number,
    taskId?: number,
    tagIds?: number[],
  ) => {
    setIsLoading(true);
    try {
      const response = await timeClockAPI.start(projectId, taskId, tagIds);
      const timer = response.data.time_clock_entry;
      setActiveTimer(timer);
      setElapsedSeconds(calculateElapsedSeconds(timer));
    } catch (error: any) {
      console.error('Failed to start timer:', error);
      throw new Error(error.response?.data?.error || 'Failed to start timer');
    } finally {
      setIsLoading(false);
    }
  };

  // Stop the active timer
  const stopTimer = async () => {
    if (!activeTimer) return;

    setIsLoading(true);
    try {
      const response = await timeClockAPI.stop(activeTimer.id);
      const timer = response.data.time_clock_entry;
      setActiveTimer(timer);
      setElapsedSeconds(calculateElapsedSeconds(timer));
      setShowCompletionModal(true);
    } catch (error: any) {
      console.error('Failed to stop timer:', error);
      throw new Error(error.response?.data?.error || 'Failed to stop timer');
    } finally {
      setIsLoading(false);
    }
  };

  // Set or clear the task on the active timer
  const setTimerTask = async (taskId: number | null) => {
    if (!activeTimer) return;

    try {
      const response = await timeClockAPI.update(activeTimer.id, {
        task_id: taskId,
      });
      setActiveTimer(response.data.time_clock_entry);
    } catch (error: any) {
      console.error('Failed to update timer task:', error);
      throw new Error(
        error.response?.data?.error || 'Failed to update timer task',
      );
    }
  };

  // Discard (delete) the active timer
  const discardTimer = async () => {
    if (!activeTimer) return;

    setIsLoading(true);
    try {
      await timeClockAPI.delete(activeTimer.id);
      setActiveTimer(null);
      setElapsedSeconds(0);
      setShowCompletionModal(false);
    } catch (error: any) {
      console.error('Failed to discard timer:', error);
      throw new Error(error.response?.data?.error || 'Failed to discard timer');
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh timer state from server
  const refreshTimer = async () => {
    await fetchCurrentTimer();
  };

  // Modal controls
  const openCompletionModal = () => setShowCompletionModal(true);
  const closeCompletionModal = () => setShowCompletionModal(false);

  // Called when timer is successfully converted to time entry
  const onTimerCompleted = () => {
    setActiveTimer(null);
    setElapsedSeconds(0);
    setShowCompletionModal(false);
  };

  const value: TimerContextType = {
    activeTimer,
    isLoading,
    elapsedSeconds,
    showCompletionModal,
    startTimer,
    stopTimer,
    setTimerTask,
    discardTimer,
    refreshTimer,
    openCompletionModal,
    closeCompletionModal,
    onTimerCompleted,
  };

  return (
    <TimerContext.Provider value={value}>{children}</TimerContext.Provider>
  );
};
